<script setup lang="ts">
import { XIcon } from 'lucide-vue-next'

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl'

const {
  id,
  title = '',
  size = 'md',
  loading = false,
  noFooter = false,
} = defineProps<{
  title?: string
  size?: ModalSize
  id: string
  loading?: boolean
  noFooter?: boolean
}>()

const open = defineModel<boolean>()

function closeModal() {
  open.value = false
}

function handleEscapeKey(event: KeyboardEvent) {
  if (event.key === 'Escape' && open.value) {
    closeModal()
  }
}

watch(open, (isOpen) => {
  if (isOpen) {
    document.addEventListener('keydown', handleEscapeKey)
  } else {
    document.removeEventListener('keydown', handleEscapeKey)
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscapeKey)
})

const modalSizeClasses = computed(() => {
  const sizeMap = {
    sm: 'max-w-xl max-h-[60vh]',
    md: 'max-w-2xl max-h-[80vh]',
    lg: 'max-w-3xl max-h-[80vh]',
    xl: 'max-w-4xl max-h-[80vh]',
    '2xl': 'max-w-5xl max-h-[80vh]',
    '3xl': 'max-w-6xl max-h-[80vh]',
    '4xl': 'max-w-7xl max-h-[80vh]',
  }
  return sizeMap[size] || sizeMap.md
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        :id="id"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-[rgba(84,48,217,0.3)]"
          @click="closeModal"
        />

        <!-- Modal Content with Transition -->
        <Transition
          enter-active-class="duration-300 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="duration-200 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="open"
            class="relative bg-base-100 rounded-2xl shadow-xl flex flex-col z-10 w-full"
            :class="modalSizeClasses"
          >
            <div v-if="!loading" class="flex flex-col min-h-0">
              <!-- Header -->
              <div class="flex-none px-6 py-4 flex justify-between items-center">
                <h3
                  id="modal-title"
                  class="text-lg font-medium leading-6 text-base-content"
                >
                  <slot name="title">
                    {{ title }}
                  </slot>
                </h3>
                <EButton
                  class="btn-circle btn-ghost btn-sm"
                  :icon="XIcon"
                  @click="closeModal"
                />
              </div>
              <!-- Scrollable Content -->
              <div class="flex-1 overflow-y-auto p-6 min-h-0">
                <slot />
              </div>
              <!-- Footer -->
              <div v-if="!noFooter" class="flex-none px-6 py-4">
                <div class="flex justify-end gap-3">
                  <slot name="footer">
                    <!-- Default footer content if needed -->
                  </slot>
                </div>
              </div>
            </div>
            <div v-else class="p-12 flex justify-center">
              <Loader />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Modal transitions and styling */
</style>
